import { useEndImpersonation } from "@/hooks/user-management/useUserManagementMutations";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import { Banner, Button } from "@onengine/component-library";
import { IconUsers } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";

function formatElapsed(seconds: number): string {
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

const IMPERSONATION_BANNER_HEIGHT = 66.1953;

export function ImpersonationBanner() {
  const { startedAt, impersonatedUserName } = useImpersonationStore();
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { mutate: endImpersonation } = useEndImpersonation();

  useEffect(() => {
    if (!startedAt) return;

    const computeElapsed = () => {
      const diffMs = Date.now() - new Date(startedAt).getTime();
      setElapsed(Math.floor(diffMs / 1000));
    };

    computeElapsed();
    intervalRef.current = setInterval(computeElapsed, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startedAt]);

  const handleLogOut = () => {
    endImpersonation();
  };

  return (
    <div
      data-slot={"impersonation-banner-container"}
      style={{
        position: "relative",
        height: IMPERSONATION_BANNER_HEIGHT,
      }}
    >
      <div
        data-slot={"impersonation-banner"}
        style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 10 }}
      >
        <Banner
          contrast="high"
          intent="info"
          fullWidth
          leadingIcon={<IconUsers size={24} />}
          title={`You are impersonating ${impersonatedUserName} - ${formatElapsed(elapsed)}`}
          message="To end this session, log out."
          dismissible
          rightAction={
            <Button variant="primary" onClick={handleLogOut}>
              Log Out
            </Button>
          }
          onClose={handleLogOut}
        />
      </div>
    </div>
  );
}
